import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import {
    PatchProjectTaskPhase,
    DeleteProjectTaskPhase,
    ReorderProjectTaskPhases,
    GetProjectTaskPhases,
    PatchTask,
    setProjectPhaseManageModalOpen,
} from '../../redux/taskSlice';
import { normalizeProjectPhaseSortableId, sortProjectPhases } from '../../utils/projectPhaseUtils';
import { Dismiss16Regular, Edit16Regular, Delete16Regular } from '@fluentui/react-icons';
import { DndContext, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SmartPointerSensor } from './dndSensors';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import SimpleAlertModal from '../CommonComponents/DoubleConfirmationModal/SimpleAlertModal';
import styles from './css/index.module.css';

const SortablePhaseManageRow = ({
    phase,
    editingPhaseId,
    editingPhaseName,
    phaseActionLoadingId,
    isReorderingPhases,
    onStartEditPhase,
    onCancelEditPhase,
    onSaveEditPhase,
    setEditingPhaseName,
    onRequestDeletePhase,
}) => {
    const isEditingThis = editingPhaseId === phase.id;
    const canDrag = phase.isProjectPhase && !isEditingThis && !phaseActionLoadingId && !isReorderingPhases;
    const sortableId = normalizeProjectPhaseSortableId(phase.id);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: sortableId,
        disabled: !canDrag,
    });

    return (
        <div
            ref={setNodeRef}
            className={`${styles.tagMenuItem} ${isDragging ? styles.phaseRowDragging : ''}`}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                transform: CSS.Transform.toString(transform),
                transition,
            }}>
            {isEditingThis ? (
                <input
                    type="text"
                    className={styles.tagInput}
                    style={{ flex: 1 }}
                    value={editingPhaseName}
                    onChange={e => setEditingPhaseName(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onSaveEditPhase(phase);
                        } else if (e.key === 'Escape') {
                            e.preventDefault();
                            onCancelEditPhase();
                        }
                    }}
                    onBlur={() => onSaveEditPhase(phase)}
                    autoFocus
                />
            ) : (
                <>
                    {phase.isProjectPhase ? (
                        <span className={styles.phaseDragHandle} aria-label="Drag to reorder" {...listeners} {...attributes}>
                            ⋮⋮
                        </span>
                    ) : (
                        <span style={{ width: 18 }} />
                    )}
                    <span style={{ flex: 1 }}>{phase.name}</span>
                </>
            )}
            {!isEditingThis && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Edit16Regular
                        style={{
                            color: 'var(--Neutral-Gray-6)',
                            cursor: phaseActionLoadingId ? 'not-allowed' : 'pointer',
                            opacity: phaseActionLoadingId === phase.id ? 0.5 : 1,
                        }}
                        onClick={() => {
                            if (phaseActionLoadingId) return;
                            onStartEditPhase(phase);
                        }}
                    />
                    <Delete16Regular
                        style={{
                            color: 'var(--Neutral-Gray-6)',
                            cursor: phaseActionLoadingId ? 'not-allowed' : 'pointer',
                            opacity: phaseActionLoadingId === phase.id ? 0.5 : 1,
                        }}
                        onClick={() => {
                            if (phaseActionLoadingId) return;
                            onRequestDeletePhase(phase);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const ProjectPhasesManageModal = () => {
    const dispatch = useDispatch();
    const { projectPhases, taskData: allProjectTasks, projectPhaseManageModalOpen } = useSelector(state => state.task);
    const [modalPhases, setModalPhases] = useState([]);
    const [isReorderingPhases, setIsReorderingPhases] = useState(false);
    const [editingPhaseId, setEditingPhaseId] = useState(null);
    const [editingPhaseName, setEditingPhaseName] = useState('');
    const [phaseActionLoadingId, setPhaseActionLoadingId] = useState(null);
    const [phasePendingDelete, setPhasePendingDelete] = useState(null);
    const [cannotDeletePhaseName, setCannotDeletePhaseName] = useState('');
    const deleteModalRef = useRef(null);

    const phaseSensors = useSensors(
        useSensor(SmartPointerSensor, {
            activationConstraint: { distance: 4 },
        }),
    );

    const sortablePhaseIds = useMemo(
        () => modalPhases.filter(phase => phase.isProjectPhase).map(phase => normalizeProjectPhaseSortableId(phase.id)),
        [modalPhases],
    );

    const getAllPhases = () => {
        const phaseMap = new Map();

        if (projectPhases && projectPhases.length > 0) {
            projectPhases.forEach(phase => {
                phaseMap.set(phase.name, { ...phase, source: 'project' });
            });
        }

        const tasks = Array.isArray(allProjectTasks) ? allProjectTasks : [];
        tasks.forEach(task => {
            const phaseName = task?.project_phase;
            if (phaseName && phaseName.trim() !== '' && !phaseMap.has(phaseName)) {
                phaseMap.set(phaseName, {
                    id: `task_${phaseName}`,
                    name: phaseName,
                    source: 'task',
                });
            }
        });

        const values = Array.from(phaseMap.values());
        const projectPhaseList = sortProjectPhases(values.filter(phase => phase.source === 'project'));
        const taskOnlyList = values
            .filter(phase => phase.source === 'task')
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return [...projectPhaseList, ...taskOnlyList];
    };

    const allPhases = getAllPhases();

    const buildManagedPhases = () => {
        const list = [];
        const projectPhaseByName = new Map();

        (projectPhases || []).forEach(phase => {
            if (!phase?.name) return;
            projectPhaseByName.set(phase.name, phase);
        });

        const inferredOnlyNames = new Set();
        allPhases.forEach(phase => {
            if (!phase?.name) return;
            if (phase.source === 'task' && !projectPhaseByName.has(phase.name)) {
                inferredOnlyNames.add(phase.name);
            }
        });

        sortProjectPhases(Array.from(projectPhaseByName.values())).forEach(phase => {
            list.push({
                id: phase.id,
                name: phase.name,
                isProjectPhase: true,
                order: phase.order,
            });
        });

        Array.from(inferredOnlyNames)
            .sort((a, b) => a.localeCompare(b))
            .forEach(name => {
                list.push({
                    id: `task_only_${name}`,
                    name,
                    isProjectPhase: false,
                });
            });

        return list;
    };

    useEffect(() => {
        if (!projectPhaseManageModalOpen) return;
        setModalPhases(buildManagedPhases());
    }, [projectPhaseManageModalOpen]);

    useEffect(() => {
        if (!projectPhaseManageModalOpen || isReorderingPhases) return;
        setModalPhases(buildManagedPhases());
    }, [projectPhases, projectPhaseManageModalOpen, isReorderingPhases]);

    const isPhaseInUse = phase => {
        if (!phase || !phase.name) return false;
        const normalizedTarget = phase.name.toString().trim().toLowerCase();
        if (!normalizedTarget) return false;

        const sourceTasks = Array.isArray(allProjectTasks) ? allProjectTasks : [];
        return sourceTasks.some(task => {
            const taskPhase = (task.project_phase || '').toString().trim().toLowerCase();
            return taskPhase === normalizedTarget;
        });
    };

    const closeModal = () => {
        dispatch(setProjectPhaseManageModalOpen(false));
        setEditingPhaseId(null);
        setEditingPhaseName('');
    };

    const handleStartEditPhase = phase => {
        if (!phase) return;
        setEditingPhaseId(phase.id);
        setEditingPhaseName(phase.name || '');
    };

    const handleCancelEditPhase = () => {
        setEditingPhaseId(null);
        setEditingPhaseName('');
    };

    const handleSaveEditPhase = async phase => {
        if (!phase) return;
        const trimmed = editingPhaseName.trim();
        if (!trimmed || trimmed === phase.name) {
            handleCancelEditPhase();
            return;
        }

        const oldName = (phase.name || '').toString();
        const newName = trimmed;

        try {
            setPhaseActionLoadingId(phase.id);

            if (phase.isProjectPhase) {
                await dispatch(
                    PatchProjectTaskPhase({
                        id: phase.id,
                        body: { name: newName },
                    }),
                );
            }

            const sourceTasks = Array.isArray(allProjectTasks) ? allProjectTasks : [];
            const normalizedOld = oldName.toString().trim().toLowerCase();
            const patchPromises = [];

            sourceTasks.forEach(task => {
                const taskPhase = (task.project_phase || '').toString().trim().toLowerCase();
                if (taskPhase !== normalizedOld) return;

                patchPromises.push(
                    dispatch(
                        PatchTask({
                            body: { project_phase: newName },
                            id: task.id,
                            suppressToast: true,
                        }),
                    ),
                );
            });

            if (patchPromises.length > 0) {
                await Promise.all(patchPromises);
            }

            handleCancelEditPhase();
        } catch (error) {
            console.error('Error updating project phase:', error);
        } finally {
            setPhaseActionLoadingId(null);
        }
    };

    const persistPhaseOrder = async reordered => {
        const orderedIds = reordered.filter(phase => phase.isProjectPhase).map(phase => phase.id);
        if (orderedIds.length < 2) return;

        setIsReorderingPhases(true);
        try {
            await dispatch(ReorderProjectTaskPhases(orderedIds)).unwrap();
        } catch (error) {
            console.error('Error reordering project phases:', error);
            await dispatch(GetProjectTaskPhases());
            setModalPhases(buildManagedPhases());
        } finally {
            setIsReorderingPhases(false);
        }
    };

    const handlePhaseDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id || isReorderingPhases) return;

        const projectPhasesInModal = modalPhases.filter(phase => phase.isProjectPhase);
        const oldIndex = projectPhasesInModal.findIndex(
            phase => normalizeProjectPhaseSortableId(phase.id) === normalizeProjectPhaseSortableId(active.id),
        );
        const newIndex = projectPhasesInModal.findIndex(
            phase => normalizeProjectPhaseSortableId(phase.id) === normalizeProjectPhaseSortableId(over.id),
        );
        if (oldIndex < 0 || newIndex < 0) return;

        const reorderedProjectPhases = arrayMove(projectPhasesInModal, oldIndex, newIndex);
        const taskOnlyPhases = modalPhases.filter(phase => !phase.isProjectPhase);
        const reordered = [...reorderedProjectPhases, ...taskOnlyPhases];

        setModalPhases(reordered);
        await persistPhaseOrder(reordered);
    };

    const handleDeletePhase = async phase => {
        if (!phase || !phase.isProjectPhase) return;

        if (isPhaseInUse(phase)) {
            setCannotDeletePhaseName(phase.name || '');
            return;
        }

        try {
            setPhaseActionLoadingId(phase.id);
            await dispatch(DeleteProjectTaskPhase(phase.id));
            if (editingPhaseId === phase.id) {
                handleCancelEditPhase();
            }
        } catch (error) {
            console.error('Error deleting project phase:', error);
        } finally {
            setPhaseActionLoadingId(null);
        }
    };

    return (
        <>
            {cannotDeletePhaseName && (
                <SimpleAlertModal
                    open={!!cannotDeletePhaseName}
                    onClose={() => setCannotDeletePhaseName('')}
                    message="This project phase cannot be deleted because it is currently associated with one or more tasks."
                />
            )}
            <DoubleConfirmationModal
                message="Deleting a Project Phase permanently removes it from the Project, and this is irreversible."
                handleAction={() => {
                    if (phasePendingDelete) {
                        handleDeletePhase(phasePendingDelete);
                    }
                }}>
                <span ref={deleteModalRef} />
            </DoubleConfirmationModal>

            <Modal
                centered
                show={projectPhaseManageModalOpen}
                onHide={closeModal}
                className={styles.modalMainBody}
                backdrop="static"
                enforceFocus={false}>
                <Modal.Body className={styles.confirmationModalBody}>
                    <div className={styles.confirmationModalMsg} style={{ width: '100%' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className={styles.tableFilterHeader + ' mb-0'}>Project Phases</h6>
                            <button
                                type="button"
                                onClick={closeModal}
                                className={styles.closeButton || 'btn btn-link p-0'}
                                style={{ border: 'none', background: 'transparent' }}>
                                <Dismiss16Regular color="var(--Brand-Primary)" />
                            </button>
                        </div>
                        <p className="mb-3" style={{ fontSize: 12, color: 'var(--Neutral-Gray-6)' }}>
                            Drag to reorder. This order is used in the phase picker and Group by view.
                        </p>

                        {modalPhases.length > 0 ? (
                            <DndContext sensors={phaseSensors} collisionDetection={closestCenter} onDragEnd={handlePhaseDragEnd}>
                                <SortableContext items={sortablePhaseIds} strategy={verticalListSortingStrategy}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {modalPhases.map(phase => (
                                            <SortablePhaseManageRow
                                                key={phase.id}
                                                phase={phase}
                                                editingPhaseId={editingPhaseId}
                                                editingPhaseName={editingPhaseName}
                                                phaseActionLoadingId={phaseActionLoadingId}
                                                isReorderingPhases={isReorderingPhases}
                                                onStartEditPhase={handleStartEditPhase}
                                                onCancelEditPhase={handleCancelEditPhase}
                                                onSaveEditPhase={handleSaveEditPhase}
                                                setEditingPhaseName={setEditingPhaseName}
                                                onRequestDeletePhase={phaseItem => {
                                                    if (isPhaseInUse(phaseItem)) {
                                                        setCannotDeletePhaseName(phaseItem.name || '');
                                                        return;
                                                    }
                                                    setPhasePendingDelete(phaseItem);
                                                    deleteModalRef.current && deleteModalRef.current.click();
                                                }}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        ) : (
                            <p className="mb-0">No project phases available.</p>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ProjectPhasesManageModal;
